// Phase 44.13 — Body scroll lock hook per modal/overlay mobile-first.
//
// Quando un modal è aperto su iPhone Safari il body sotto deve essere
// completamente bloccato per evitare:
//   - scroll/overscroll bounce visibile dietro la modale
//   - touch chaining (swipe sulla mappa con modale aperto)
//   - sensazione di "instabilità" tipica delle webapp non-native
//
// Approccio semplice e robusto (compatibile con figli position:fixed):
//   - documentElement (html).overflow = "hidden"
//   - body.overflow = "hidden"
//   - body.touchAction = "none" → blocca pan/zoom touch su iOS/Android
//   - paddingRight compensato per scrollbar desktop (no layout shift)
//
// PERCHÉ NON usiamo `position:fixed + top:-scrollY`: in Chromium quel pattern
// (oft consigliato) sposta involontariamente i discendenti `position: fixed`
// del valore di `top` negativo, ancorando i modal fuori dal viewport visibile.
// Bug verificato sul reale stack DapNow.
//
// Gestione modali annidati: counter `lockCount` evita rilasci prematuri.
import { useEffect } from "react";

let lockCount = 0;
let savedStyles = null;

function getScrollbarWidth() {
  if (typeof window === "undefined") return 0;
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

function applyLock() {
  if (lockCount > 0) {
    lockCount += 1;
    return;
  }
  if (typeof window === "undefined") return;
  const body = document.body;
  const html = document.documentElement;
  const sbw = getScrollbarWidth();
  savedStyles = {
    bodyOverflow: body.style.overflow,
    bodyTouchAction: body.style.touchAction,
    bodyPaddingRight: body.style.paddingRight,
    htmlOverflow: html.style.overflow,
  };
  body.style.overflow = "hidden";
  body.style.touchAction = "none";
  if (sbw > 0) body.style.paddingRight = `${sbw}px`;
  html.style.overflow = "hidden";
  // Phase 44.20 — flag centralizzato per nascondere la bottom-nav (e altri
  // elementi fixed competitor) quando un modal è aperto. Letto via CSS in
  // index.css → niente race con React state, niente prop drilling.
  body.setAttribute("data-modal-open", "true");
  lockCount = 1;
}

function releaseLock() {
  if (lockCount <= 0) return;
  lockCount -= 1;
  if (lockCount > 0) return;
  if (typeof window === "undefined" || !savedStyles) return;
  const body = document.body;
  const html = document.documentElement;
  body.style.overflow = savedStyles.bodyOverflow;
  body.style.touchAction = savedStyles.bodyTouchAction;
  body.style.paddingRight = savedStyles.bodyPaddingRight;
  html.style.overflow = savedStyles.htmlOverflow;
  body.removeAttribute("data-modal-open");
  savedStyles = null;
}

export function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    applyLock();
    return releaseLock;
  }, [active]);
}
